// ── Qur'anic allusion score lookup ────────────────────────────────────────────
// Zero-cost, static poet-level scoring based on scholarly consensus about each
// poet's systematic engagement with Qur'anic imagery, diction, and allusion.
//
// Scale: 0–10 integer
//   0   Pre-Islamic — allusions impossible by definition
//   1–2 Secular / libertine — incidental Islamic vocabulary at most
//   3–4 Classical mainstream — occasional Qur'anic echo, not systematic
//   5–6 Moderate engagement — uses Qur'anic register deliberately at times
//   7–8 High engagement — frequent, intentional allusion across the corpus
//   9–10 Systematic — Qur'anic imagery is constitutive of the poetic project
//
// A score ≥ 5 triggers the hint banner in the Ask AI card.
// Matching: normalized (tashkeel-stripped, alef-unified) exact → partial.
// Unknown poets default to 2 (conservative — avoids false hints).

// ── Normalisation ─────────────────────────────────────────────────────────────

function norm(s) {
  return s
    .replace(/[ً-ٰٟ]/g, '') // strip tashkeel / harakat
    .replace(/[أإآ]/g, 'ا')                  // unify alef variants
    .replace(/\s+/g, ' ')
    .trim();
}

// ── Score table ───────────────────────────────────────────────────────────────
// Keys are canonical Arabic poet names as they commonly appear in the DB.

const SCORES = {

  // ── Pre-Islamic (Jāhilī) — score 0 ──────────────────────────────────────────
  'امرؤ القيس':           0,
  'امرئ القيس':           0,
  'طرفة بن العبد':        0,
  'عنترة بن شداد':        0,
  'عنترة':                0,
  'زهير بن أبي سلمى':     0,
  'النابغة الذبياني':     0,
  'الأعشى':               0,
  'لبيد بن ربيعة':        0,
  'الشنفرى':              0,
  'تأبط شرًا':            0,
  'المهلهل':              0,
  'سلامة بن جندل':        0,
  'أوس بن حجر':           0,
  'علقمة الفحل':          0,
  'الحارث بن حلزة':       0,
  'عمرو بن كلثوم':        0,
  'المثقب العبدي':        0,
  'قيس بن الخطيم':        0,
  'دريد بن الصمة':        0,
  'أبو ذؤيب الهذلي':      0,
  'الخنساء':              2,   // elegist; converted but wrote grief-poetry, not allusion

  // ── Early Islamic ────────────────────────────────────────────────────────────
  'حسان بن ثابت':         9,   // companion poet; panegyric for the Prophet
  'كعب بن زهير':          5,   // Bānat Suʿād — explicit conversion poem
  'كعب بن مالك':          7,
  'عبد الله بن رواحة':    8,   // companion; praised jihad in Qur'anic register
  'الحطيئة':              2,
  'النابغة الجعدي':       3,

  // ── Umayyad ──────────────────────────────────────────────────────────────────
  'الفرزدق':              3,
  'جرير':                 3,
  'الأخطل':               1,   // Christian — no Qur'anic allusion
  'ذو الرمة':             2,
  'عمر بن أبي ربيعة':     2,
  'الأحوص':               2,
  'الكميت الأسدي':        7,   // Hāshimiyyāt — explicit Qur'anic citation
  'سراقة البارقي':        3,
  'قيس بن الملوح':        2,   // Majnūn Laylā — romantic, not religious
  'جميل بثينة':           2,
  'كثير عزة':             3,
  'الأقيشر الأسدي':       1,
  'الوليد بن يزيد':       1,   // known libertine
  'بشر بن أبي خازم':      1,

  // ── Abbasid ──────────────────────────────────────────────────────────────────
  'بشار بن برد':          2,
  'أبو نواس':             2,   // wine poetry; parodies Qur'anic register
  'أبو العتاهية':         9,   // zuhd poetry; most Qur'anically saturated Abbasid poet
  'مسلم بن الوليد':       3,
  'أبو تمام':             5,   // classicist; deliberate Qur'anic diction in panegyric
  'البحتري':              4,
  'ابن الرومي':           4,
  'ابن المعتز':           4,
  'المتنبي':              5,   // Qur'anic register/vocabulary, not citation allusion
  'أبو فراس الحمداني':    4,
  'الشريف الرضي':         8,   // Shia jurist/poet; quotes Qur'an directly
  'الشريف المرتضى':       7,
  'أبو العلاء المعري':    5,   // philosophical engagement with Qur'anic text
  'مهيار الديلمي':        6,
  'الحسين بن علي':        4,
  'ديك الجن':             2,
  'صريع الغواني':         2,

  // ── Islamic scholars / religious poets ───────────────────────────────────────
  'الشافعي':              8,   // juristic Arabic poetry, Qur'anic references
  'الإمام الشافعي':       8,
  'ابن القيم الجوزية':    9,
  'ابن القيم':            9,
  'ابن تيمية':            8,
  'الغزالي':              7,
  'ابن الجوزي':           7,
  'ابن عقيل':             6,

  // ── Sufi poets — highest scores ──────────────────────────────────────────────
  'ابن الفارض':           9,   // Tāʾiyya: Qur'anic symbolism is the structural logic
  'ابن عربي':             9,   // Fusūs; Tarjumān al-Ashwāq — dense Qur'anic cosmology
  'البوصيري':             9,   // Burda / Hamziyya — sustained Prophetic/Qur'anic motifs
  'ابن عطاء الله السكندري': 8,
  'رابعة العدوية':        7,
  'الحلاج':               7,
  'الجنيد البغدادي':      6,

  // ── Andalusian ───────────────────────────────────────────────────────────────
  'ابن زيدون':            4,
  'ابن حزم':              5,
  'لسان الدين ابن الخطيب': 5,
  'ابن خفاجة':            3,
  'ابن قزمان':            2,
  'ابن عبد ربه':          4,
  'صفي الدين الحلي':      6,
  'ابن سهل الإشبيلي':     4,
  'ابن حمديس':            3,
  'الأعمى التطيلي':       3,

  // ── Mamluk / late classical ──────────────────────────────────────────────────
  'ابن نباتة المصري':     5,
  'شهاب الدين محمود':     5,
  'البهاء زهير':          4,
  'الأبوصيري':            9,   // alternate spelling
  'ابن دانيال':           3,

  // ── Neo-classical / Nahda ────────────────────────────────────────────────────
  'أحمد شوقي':            5,   // Prince of Poets; uses Qur'anic diction systematically
  'حافظ إبراهيم':         4,
  'جميل صدقي الزهاوي':    3,
  'معروف الرصافي':        3,
  'خليل مطران':           3,

  // ── Modern Arabic poetry ──────────────────────────────────────────────────────
  'إيليا أبو ماضي':       2,
  'نزار قباني':           2,
  'محمود درويش':          3,
  'أبو القاسم الشابي':    4,
  'عمر أبو ريشة':         3,
  'بدر شاكر السياب':      3,
  'صلاح عبد الصبور':      3,
  'أحمد عبد المعطي حجازي': 3,
  'أمل دنقل':             3,
  'أحمد مطر':             4,
  'سميح القاسم':          3,
  'فدوى طوقان':           3,
  'نازك الملائكة':        3,
  'أحمد رامي':            4,
};

const DEFAULT_SCORE = 2;

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Returns a 0–10 Qur'anic allusion score for the given Arabic poet name.
 * Based on scholarly consensus about the poet's systematic engagement with
 * Qur'anic imagery across their corpus — not a per-verse analysis.
 *
 * Divide by 10 before passing to buildPrompt / AskAiAboutVerseCard
 * (those APIs expect a 0–1 float).
 *
 * @param {string} poetAr
 * @returns {number} integer 0–10
 */
export function getQuranicAllusionScore(poetAr) {
  if (!poetAr) return DEFAULT_SCORE;
  const name = norm(poetAr);

  // 1. Exact normalised match
  for (const [key, score] of Object.entries(SCORES)) {
    if (norm(key) === name) return score;
  }

  // 2. Substring match — name contains key or key contains name
  for (const [key, score] of Object.entries(SCORES)) {
    const nk = norm(key);
    if (name.includes(nk) || nk.includes(name)) return score;
  }

  return DEFAULT_SCORE;
}
